import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { PageMeta } from "@/components/site/PageMeta";
import { Reveal } from "@/components/site/Reveal";
import { CTABanner } from "@/components/site/CTABanner";
import { PhotoBackground } from "@/components/site/PhotoBackground";
import { INSIGHTS } from "@/data/content";

const ARTICLE_IMG =
    "https://images.unsplash.com/photo-1609810074355-29137827b2c4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHxkZXNlcnQlMjBtb3VudGFpbiUyMHN1bnNldHxlbnwwfHx8MTc5MDk4ODc0N3ww&ixlib=rb-4.1.0&q=85";

export default function InsightArticle() {
    const { slug } = useParams();
    const reduce = useReducedMotion();
    const index = INSIGHTS.findIndex((a) => a.slug === slug);
    if (index === -1) return <Navigate to="/insights" replace />;
    const article = INSIGHTS[index];
    const next = INSIGHTS[(index + 1) % INSIGHTS.length];

    return (
        <>
            <PageMeta title={`${article.title} | Nationwide Strategies LLC`} description={article.excerpt} />
            <article data-testid="insight-article" className="pt-32 sm:pt-40">
                <div className="relative overflow-hidden">
                    <PhotoBackground src={ARTICLE_IMG} eager />
                    <div className="relative mx-auto max-w-3xl px-4 pb-16 sm:px-6">
                        <motion.div
                            initial={reduce ? false : { opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        >
                            <Link
                                to="/insights"
                                data-testid="article-back-link"
                                className="group inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 transition-colors duration-300 hover:text-slate-100"
                            >
                                <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
                                All Insights
                            </Link>
                            <div className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                <span className="text-blue-400">{article.category}</span>
                                <span>{article.date}</span>
                                <span>{article.readTime}</span>
                            </div>
                            <h1 className="mt-6 font-serif text-4xl font-medium leading-[1.1] tracking-tight text-slate-100 sm:text-5xl">
                                {article.title}
                            </h1>
                            <p className="mt-8 border-l-2 border-blue-500/60 pl-6 font-serif text-xl italic leading-relaxed text-slate-300">
                                {article.excerpt}
                            </p>
                        </motion.div>
                    </div>
                </div>

                <div className="mx-auto max-w-3xl px-4 sm:px-6">
                    <div className="mt-16 space-y-16 pb-20">
                        {article.body.map((section, i) => (
                            <Reveal key={section.heading} delay={0.05}>
                                <section>
                                    <h2 className="flex items-baseline gap-4 font-serif text-2xl font-medium tracking-tight text-slate-100 sm:text-3xl">
                                        <span className="font-mono text-xs tracking-[0.25em] text-amber-500">
                                            §{i + 1}
                                        </span>
                                        {section.heading}
                                    </h2>
                                    <div className="mt-6 space-y-6">
                                        {section.paragraphs.map((p, j) => (
                                            <p key={j} className="text-base leading-relaxed text-slate-400">
                                                {p}
                                            </p>
                                        ))}
                                    </div>
                                </section>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal className="mb-24 border border-amber-500/70 bg-[#0b0f16]/60 p-8 transition-colors duration-300 hover:border-amber-400">
                        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500">
                            Next Briefing
                        </p>
                        <Link
                            to={`/insights/${next.slug}`}
                            data-testid="article-next-link"
                            className="group mt-5 flex items-center justify-between gap-6"
                        >
                            <span className="font-serif text-2xl font-medium leading-snug tracking-tight text-slate-100 transition-colors duration-300 group-hover:text-white sm:text-3xl">
                                {next.title}
                            </span>
                            <ArrowRight size={20} className="shrink-0 text-slate-500 transition-all duration-300 group-hover:translate-x-1 group-hover:text-amber-400" />
                        </Link>
                    </Reveal>
                </div>
            </article>
            <CTABanner />
        </>
    );
}
